import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import {
  Camera,
  Trash2,
  Loader2,
  Image as ImageIcon,
  Crop as CropIcon,
  UploadCloud,
  X,
  Library,
  Search,
  Check,
  Film,
  ArrowUp,
  ArrowDown,
  Plus,
  FolderOpen,
  ArrowLeft,
  HardDrive,
  GripVertical,
} from "lucide-react";
import { sb } from "@/lib/sb";
import {
  updateSitePhotoAlt,
  clearSitePhoto,
} from "@/lib/site-photos.functions";
import {
  uploadMediaItem,
  deleteMediaItem,
  assignMediaToSlot,
  removeDuplicateMedia,
  uploadAndAssignToSlot,
} from "@/lib/media-library.functions";


import { saveSlideshow, type SlideshowConfig } from "@/lib/slideshows.functions";
import {
  listDriveFiles,
  fetchDriveFile,
  type DriveFile,
} from "@/lib/google-drive.functions";
import { encodeForUpload } from "@/lib/image-encode";
import { invalidateSitePhotoCache } from "@/components/SitePhoto";
import { CropModal } from "@/components/CropModal";

export const Route = createFileRoute("/_authenticated/dashboard/site-photos")({
  component: SitePhotosPage,
});

type Photo = {
  id: string;
  slot_key: string;
  page_path: string;
  label: string;
  description: string | null;
  original_url: string | null;
  webp_url: string | null;
  alt: string | null;
  width: number | null;
  height: number | null;
  position: number;
  media_id: string | null;
};

type MediaItem = {
  id: string;
  filename: string;
  original_url: string;
  webp_url: string;
  width: number | null;
  height: number | null;
  size_bytes: number | null;
  created_at: string;
};

const PAGES: { path: string; label: string }[] = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/contact", label: "Contact" },
];

function SitePhotosPage() {
  const [tab, setTab] = useState<"placements" | "library">("placements");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [library, setLibrary] = useState<MediaItem[]>([]);
  const [activePage, setActivePage] = useState<string>("/");
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [pickerForSlot, setPickerForSlot] = useState<Photo | null>(null);
  const [cropping, setCropping] = useState<
    | { slot: Photo; file: File; mediaId: string | null }
    | null
  >(null);

  const saveAlt = useServerFn(updateSitePhotoAlt);
  const clearOne = useServerFn(clearSitePhoto);
  const assignFn = useServerFn(assignMediaToSlot);

  const loadPhotos = () =>
    sb.from("site_photos")
      .select("*")
      .order("page_path", { ascending: true })
      .order("position", { ascending: true })
      .then(({ data }: { data: Photo[] | null }) => setPhotos(data ?? []));

  const loadLibrary = () =>
    sb.from("media_library")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }: { data: MediaItem[] | null }) => setLibrary(data ?? []));

  useEffect(() => {
    loadPhotos();
    loadLibrary();
  }, []);

  const filtered = useMemo(
    () => photos.filter((p) => p.page_path === activePage),
    [photos, activePage],
  );

  const handleAlt = async (slot: Photo, alt: string) => {
    setPhotos((p) => p.map((x) => (x.slot_key === slot.slot_key ? { ...x, alt } : x)));
    try {
      await saveAlt({ data: { slotKey: slot.slot_key, alt } });
    } catch (e: any) {
      toast.error(e?.message || "Could not save");
    }
  };

  const handleClear = async (slot: Photo) => {
    if (!confirm(`Remove photo from "${slot.label}"?`)) return;
    setBusySlot(slot.slot_key);
    try {
      await clearOne({ data: { slotKey: slot.slot_key } });
      invalidateSitePhotoCache(slot.slot_key);
      toast.success("Photo removed.");
      await loadPhotos();
    } catch (e: any) {
      toast.error(e?.message || "Could not remove");
    } finally {
      setBusySlot(null);
    }
  };

  // Pick from library → fetch original → open crop
  const handlePickFromLibrary = async (slot: Photo, item: MediaItem) => {
    setPickerForSlot(null);
    setBusySlot(slot.slot_key);
    try {
      const resp = await fetch(item.original_url);
      const blob = await resp.blob();
      const file = new File([blob], item.filename, { type: blob.type || "image/jpeg" });
      setCropping({ slot, file, mediaId: item.id });
    } catch (e: any) {
      toast.error(e?.message || "Could not load image");
    } finally {
      setBusySlot(null);
    }
  };

  // Confirm crop → assign or direct upload
  const confirmCrop = async (cropped: File) => {
    if (!cropping) return;
    const { slot, mediaId } = cropping;
    setCropping(null);
    setBusySlot(slot.slot_key);
    try {
      const encoded = await encodeForUpload(cropped);
      const variantPayload = encoded.variants.map((v) => ({ width: v.width, base64: v.base64 }));
      if (mediaId) {
        await assignFn({
          data: {
            slotKey: slot.slot_key,
            mediaId,
            croppedWebpBase64: encoded.webp.base64,
            width: encoded.width,
            height: encoded.height,
            alt: slot.alt,
            variants: variantPayload,
          },
        });
      } else {
        // Direct upload path: upload + assign in one go, without creating a
        // media_library entry (so cropped originals don't clutter the library).
        await uploadAndAssignDirectFn({
          data: {
            slotKey: slot.slot_key,
            filename: cropped.name,
            originalMime: encoded.original.mime,
            originalBase64: encoded.original.base64,
            croppedWebpBase64: encoded.webp.base64,
            width: encoded.width,
            height: encoded.height,
            alt: slot.alt,
            variants: variantPayload,
          },
        });
      }

      invalidateSitePhotoCache(slot.slot_key);
      toast.success(`Updated "${slot.label}"`);
      await loadPhotos();
    } catch (e: any) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setBusySlot(null);
    }
  };

  const uploadAndAssignDirectFn = useServerFn(uploadAndAssignToSlot);



  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
            <Camera size={22} /> Site Photos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload photos once into your Media Library, then assign them to any slot. Each slot
            gets its own crop — the original stays untouched.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("placements")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "placements"
                ? "bg-brand text-primary-foreground"
                : "bg-card border border-border"
            }`}
          >
            Page Placements
          </button>
          <button
            onClick={() => setTab("library")}
            className={`px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${
              tab === "library"
                ? "bg-brand text-primary-foreground"
                : "bg-card border border-border"
            }`}
          >
            <Library size={14} /> Media Library
            <span className="text-xs opacity-80">({library.length})</span>
          </button>
        </div>
      </div>


      {tab === "placements" ? (
        <PlacementsPanel
          activePage={activePage}
          onPageChange={setActivePage}
          photos={filtered}
          library={library}
          busySlot={busySlot}
          onPickForSlot={setPickerForSlot}
          onUploadForSlot={(slot, f) => setCropping({ slot, file: f, mediaId: null })}
          onAltForSlot={handleAlt}
          onClearForSlot={handleClear}
        />
      ) : (
        <MediaLibraryPanel
          items={library}
          onReload={loadLibrary}
          onPhotosNeedReload={loadPhotos}
        />
      )}

      {cropping && (
        <CropModal
          file={cropping.file}
          title={`Crop for — ${cropping.slot.label}`}
          onCancel={() => setCropping(null)}
          onConfirm={confirmCrop}
        />
      )}

      {pickerForSlot && (
        <LibraryPickerModal
          items={library}
          slot={pickerForSlot}
          onClose={() => setPickerForSlot(null)}
          onPick={(item) => handlePickFromLibrary(pickerForSlot, item)}
        />
      )}
    </div>
  );
}

function SlotCard({
  slot,
  busy,
  onChooseFromLibrary,
  onUploadFile,
  onAlt,
  onClear,
}: {
  slot: Photo;
  busy: boolean;
  onChooseFromLibrary: () => void;
  onUploadFile: (file: File) => void;
  onAlt: (alt: string) => void;
  onClear: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const url = slot.webp_url || slot.original_url;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col">
      <div className="aspect-video bg-surface-alt relative">
        {url ? (
          <img src={url} alt={slot.alt || slot.label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon size={28} className="opacity-60 mb-1" />
            <span className="text-xs">No photo yet — gray placeholder is shown on the live site.</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <Loader2 className="animate-spin text-brand" />
          </div>
        )}
      </div>

      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <div>
          <p className="font-display font-semibold text-ink">{slot.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <code className="px-1 py-0.5 rounded bg-muted text-[10px]">{slot.slot_key}</code>{" "}
            · on <span className="font-medium">{slot.page_path}</span>
          </p>
          {slot.description && (
            <p className="text-xs text-muted-foreground mt-2">{slot.description}</p>
          )}
        </div>

        <div>
          <label className="text-xs text-muted-foreground">Alt text (for accessibility / SEO)</label>
          <input
            value={slot.alt ?? ""}
            onChange={(e) => onAlt(e.target.value)}
            placeholder="e.g. Renovated kitchen with white shaker cabinets"
            className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2 mt-auto">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadFile(f);
              e.target.value = "";
            }}
          />
          <button
            onClick={onChooseFromLibrary}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold disabled:opacity-50"
          >
            <Library size={14} /> Choose from library
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:bg-background disabled:opacity-50"
          >
            <CropIcon size={14} /> Upload new
          </button>
          {url && (
            <button
              onClick={onClear}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:border-destructive hover:text-destructive disabled:opacity-50"
            >
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        {slot.width && slot.height && (
          <p className="text-[11px] text-muted-foreground">
            Cropped at {slot.width}×{slot.height}. Served as WebP first, original as fallback.
          </p>
        )}
      </div>
    </div>
  );
}

function MediaLibraryPanel({
  items,
  onReload,
  onPhotosNeedReload,
}: {
  items: MediaItem[];
  onReload: () => Promise<void> | void;
  onPhotosNeedReload: () => Promise<void> | void;
}) {
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [driveOpen, setDriveOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadFn = useServerFn(uploadMediaItem);
  const deleteFn = useServerFn(deleteMediaItem);
  const dedupeFn = useServerFn(removeDuplicateMedia);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const fetchDriveFn = useServerFn(fetchDriveFile);


  const importFromDrive = async (files: DriveFile[]) => {
    setDriveOpen(false);
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const df = files[i];
      try {
        const bytes = await fetchDriveFn({ data: { fileId: df.id, filename: df.name } });
        // Reconstruct a File from base64, then run the same encode pipeline.
        const bin = atob(bytes.base64);
        const arr = new Uint8Array(bin.length);
        for (let j = 0; j < bin.length; j++) arr[j] = bin.charCodeAt(j);
        const blob = new Blob([arr], { type: bytes.mime });
        const f = new File([blob], bytes.filename, { type: bytes.mime });
        const encoded = await encodeForUpload(f);
        await uploadFn({
          data: {
            filename: f.name,
            originalMime: encoded.original.mime,
            originalBase64: encoded.original.base64,
            webpBase64: encoded.webp.base64,
            width: encoded.width,
            height: encoded.height,
            sizeBytes: f.size,
            variants: encoded.variants.map((v) => ({ width: v.width, base64: v.base64 })),
          },
        });

        ok++;
      } catch (e: any) {
        toast.error(`${df.name}: ${e?.message || "import failed"}`);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    setProgress(null);
    toast.success(`Imported ${ok} of ${files.length} from Google Drive.`);
    await onReload();
  };


  // Hide per-slot cropped originals (legacy entries named "<name>-crop.<ext>")
  // — they remain in storage so the live site keeps working, but they
  // shouldn't show up in the Media Library.
  const visibleItems = useMemo(
    () => items.filter((i) => !/-crop\.[a-z0-9]+$/i.test(i.filename)),
    [items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleItems;
    return visibleItems.filter((i) => i.filename.toLowerCase().includes(q));
  }, [visibleItems, query]);


  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        return false;
      }
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name} is over 50MB`);
        return false;
      }
      return true;
    });
    if (arr.length === 0) return;

    setUploading(true);
    setProgress({ done: 0, total: arr.length });
    let ok = 0;
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      try {
        const encoded = await encodeForUpload(f);
        await uploadFn({
          data: {
            filename: f.name,
            originalMime: encoded.original.mime,
            originalBase64: encoded.original.base64,
            webpBase64: encoded.webp.base64,
            width: encoded.width,
            height: encoded.height,
            sizeBytes: f.size,
            variants: encoded.variants.map((v) => ({ width: v.width, base64: v.base64 })),
          },
        });

        ok++;
      } catch (e: any) {
        toast.error(`${f.name}: ${e?.message || "failed"}`);
      }
      setProgress({ done: i + 1, total: arr.length });
    }
    setUploading(false);
    setProgress(null);
    toast.success(`Uploaded ${ok} of ${arr.length} photos to library.`);
    await onReload();
  };

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`Delete "${item.filename}" from library? Slots using it will keep their cropped copy.`)) return;
    try {
      await deleteFn({ data: { id: item.id } });
      toast.success("Removed from library.");
      await onReload();
      await onPhotosNeedReload();
    } catch (e: any) {
      toast.error(e?.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!uploading) handleFiles(e.dataTransfer.files);
          }}
          onClick={() => !uploading && fileRef.current?.click()}
          className="flex-1 border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-brand transition-colors bg-card"
        >
          <UploadCloud className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm">
            {uploading
              ? `Uploading ${progress?.done ?? 0} / ${progress?.total ?? 0}…`
              : "Click or drop images here (JPG, PNG, WebP, up to 50MB each)"}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
        <button
          onClick={() => !uploading && setDriveOpen(true)}
          disabled={uploading}
          className="sm:w-64 border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-brand transition-colors bg-card disabled:opacity-50"
        >
          <HardDrive className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium">Import from Google Drive</p>
          <p className="text-[11px] text-muted-foreground mt-1">Browse and import photos</p>
        </button>
      </div>


      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by filename…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-card border border-border text-sm"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} of {visibleItems.length}
        </span>
        <button
          onClick={async () => {
            if (dedupeBusy) return;
            if (!confirm("Scan the library and remove duplicate photos (same filename + size + dimensions)? The oldest copy of each is kept. Duplicates still used by a slot are skipped.")) return;
            setDedupeBusy(true);
            try {
              const res: any = await dedupeFn();
              const removed = res?.removed ?? 0;
              const skipped = res?.skipped ?? 0;
              if (removed === 0 && skipped === 0) toast.success("No duplicates found.");
              else toast.success(`Removed ${removed} duplicate${removed === 1 ? "" : "s"}${skipped ? ` (skipped ${skipped} in use)` : ""}.`);
              await onReload();
            } catch (e: any) {
              toast.error(e?.message || "Dedupe failed");
            } finally {
              setDedupeBusy(false);
            }
          }}
          disabled={dedupeBusy || items.length === 0}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card text-xs font-medium hover:border-brand hover:text-brand transition-colors disabled:opacity-50"
          title="Find and delete duplicate photos in the library"
        >
          {dedupeBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          {dedupeBusy ? "Scanning…" : "Remove duplicates"}
        </button>
      </div>


      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
          {items.length === 0
            ? "Your library is empty — drop some photos above to get started."
            : "No photos match your search."}
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filtered.map((item) => (
            <div key={item.id} className="group relative bg-card border border-border rounded-lg overflow-hidden">
              <div className="aspect-square bg-surface-alt">
                <img
                  src={item.webp_url}
                  alt={item.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-2">
                <p className="text-xs truncate font-medium" title={item.filename}>
                  {item.filename}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {item.width}×{item.height}
                </p>
              </div>
              <button
                onClick={() => handleDelete(item)}
                className="absolute top-1 right-1 p-1 rounded-md bg-background/80 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                title="Delete from library"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {driveOpen && (
        <DrivePickerModal
          onClose={() => setDriveOpen(false)}
          onImport={importFromDrive}
        />
      )}
    </div>
  );
}

function LibraryPickerModal({
  items,
  slot,
  onClose,
  onPick,
}: {
  items: MediaItem[];
  slot: Photo;
  onClose: () => void;
  onPick: (item: MediaItem) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.filename.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-2xl w-full max-w-4xl flex flex-col max-h-[90vh]"
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold text-ink">
            <Library size={16} /> Choose photo for — {slot.label}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by filename…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm"
            />
          </div>
        </div>

        <div className="p-4 overflow-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              {items.length === 0
                ? "Library is empty. Open Media Library tab to upload photos first."
                : "No matches."}
            </p>
          ) : (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onPick(item)}
                  className="group relative bg-background border border-border rounded-lg overflow-hidden hover:border-brand transition-colors text-left"
                >
                  <div className="aspect-square bg-surface-alt">
                    <img
                      src={item.webp_url}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-xs truncate font-medium" title={item.filename}>
                      {item.filename}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {item.width}×{item.height}
                    </p>
                  </div>
                  <div className="absolute inset-0 bg-brand/0 group-hover:bg-brand/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-brand text-primary-foreground rounded-full p-2">
                      <Check size={18} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Unified per-page placements (slideshow spots + leftover single slots)
// ============================================================

import { SLIDESHOW_SPOTS, COVERED_SLOT_KEYS } from "@/lib/slideshow-spots";

type SlideshowMap = Record<string, SlideshowConfig | undefined>;

function PlacementsPanel({
  activePage,
  onPageChange,
  photos,
  library,
  busySlot,
  onPickForSlot,
  onUploadForSlot,
  onAltForSlot,
  onClearForSlot,
}: {
  activePage: string;
  onPageChange: (p: string) => void;
  photos: Photo[];
  library: MediaItem[];
  busySlot: string | null;
  onPickForSlot: (slot: Photo) => void;
  onUploadForSlot: (slot: Photo, file: File) => void;
  onAltForSlot: (slot: Photo, alt: string) => void;
  onClearForSlot: (slot: Photo) => void;
}) {
  const [map, setMap] = useState<SlideshowMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sb.from("app_settings")
      .select("slideshows")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }: { data: { slideshows: SlideshowMap } | null }) => {
        setMap(data?.slideshows ?? {});
        setLoading(false);
      });
  }, []);

  const spotsForPage = useMemo(
    () => SLIDESHOW_SPOTS.filter((s) => s.pagePath === activePage),
    [activePage],
  );
  const orphanSlots = useMemo(
    () => photos.filter((p) => !COVERED_SLOT_KEYS.has(p.slot_key)),
    [photos],
  );

  return (
    <div className="space-y-6">
      <div className="border-b border-border -mx-4 md:-mx-6 px-4 md:px-6 bg-card/30 sticky top-0 z-10 backdrop-blur">
        <nav className="flex gap-1 overflow-x-auto py-2">
          {PAGES.map((p) => {
            const active = activePage === p.path;
            return (
              <button
                key={p.path}
                onClick={() => onPageChange(p.path)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? "bg-brand/10 text-brand"
                    : "text-muted-foreground hover:text-foreground hover:bg-background"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </nav>
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-brand" />
        </div>
      ) : (
        <>
          {spotsForPage.length === 0 && orphanSlots.length === 0 && (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              No photo placements configured for this page yet.
            </div>
          )}

          {spotsForPage.length > 0 && (
            <div className="space-y-4">
              {spotsForPage.map((spot) => (
                <SlideshowSpotEditor
                  key={spot.key}
                  spot={spot}
                  config={map[spot.key]}
                  library={library}
                  onChange={(next) => setMap((m) => ({ ...m, [spot.key]: next }))}
                />
              ))}
            </div>
          )}

          {orphanSlots.length > 0 && (
            <div className="space-y-3">
              <div>
                <p className="font-display font-semibold text-ink">Individual photos</p>
                <p className="text-xs text-muted-foreground">
                  Single-image spots on this page (e.g. gallery thumbnails).
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {orphanSlots.map((slot) => (
                  <SlotCard
                    key={slot.slot_key}
                    slot={slot}
                    busy={busySlot === slot.slot_key}
                    onChooseFromLibrary={() => onPickForSlot(slot)}
                    onUploadFile={(f) => onUploadForSlot(slot, f)}
                    onAlt={(a) => onAltForSlot(slot, a)}
                    onClear={() => onClearForSlot(slot)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SlideshowSpotEditor({
  spot,
  config,
  library,
  onChange,
}: {
  spot: (typeof SLIDESHOW_SPOTS)[number];
  config: SlideshowConfig | undefined;
  library: MediaItem[];
  onChange: (next: SlideshowConfig) => void;
}) {
  const cfg: SlideshowConfig = config ?? { intervalMs: 5000, transitionMs: 1000, items: [] };
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveFn = useServerFn(saveSlideshow);

  const libraryById = useMemo(() => {
    const m = new Map<string, MediaItem>();
    library.forEach((it) => m.set(it.id, it));
    return m;
  }, [library]);

  const move = (i: number, dir: -1 | 1) => {
    const items = [...cfg.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    onChange({ ...cfg, items });
  };
  const remove = (i: number) =>
    onChange({ ...cfg, items: cfg.items.filter((_, idx) => idx !== i) });
  const addItems = (ids: string[]) => {
    const existing = new Set(cfg.items.map((i) => i.mediaId));
    const additions = ids.filter((id) => !existing.has(id)).map((id) => ({ mediaId: id }));
    onChange({ ...cfg, items: [...cfg.items, ...additions] });
  };
  const setSlot = (idx: number, mediaId: string) => {
    const items = [...cfg.items];
    if (idx >= items.length) items.push({ mediaId });
    else items[idx] = { ...items[idx], mediaId };
    onChange({ ...cfg, items });
  };
  const [pickingSlot, setPickingSlot] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= cfg.items.length || to >= cfg.items.length) return;
    const items = [...cfg.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    onChange({ ...cfg, items });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveFn({ data: { key: spot.key, config: cfg } });
      toast.success(`Saved “${spot.label}”.`);
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isMulti = cfg.items.length > 1;
  const firstPreview = cfg.items[0] ? libraryById.get(cfg.items[0].mediaId) : null;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-56 aspect-video sm:aspect-auto bg-surface-alt relative shrink-0">
          {firstPreview ? (
            <img src={firstPreview.webp_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground p-3 text-center">
              <ImageIcon size={24} className="opacity-60 mb-1" />
              <span className="text-xs">No photo yet — gray placeholder is shown on the live site.</span>
            </div>
          )}
          {cfg.items.length > 1 && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background/85 text-[11px] font-medium">
              <Film size={11} /> {cfg.items.length} slides
            </span>
          )}
        </div>


        <div className="p-4 flex-1 space-y-3">
          <div>
            <p className="font-display font-semibold text-ink">{spot.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{spot.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-background border border-border">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mr-1">Slides:</span>
            {cfg.items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setPickingSlot(idx)}
                className="px-3 py-1.5 rounded-md border border-border bg-card text-xs font-semibold hover:bg-surface-alt"
                title={`Replace slide ${idx + 1}`}
              >
                Slide {idx + 1}
              </button>
            ))}
            <button
              onClick={() => setPickingSlot(cfg.items.length)}
              className="px-3 py-1.5 rounded-md bg-brand text-primary-foreground text-xs font-semibold hover:bg-brand-hover inline-flex items-center gap-1"
              title="Add a new slide"
            >
              <Plus size={14} /> Add Slide {cfg.items.length + 1}
            </button>
          </div>



          {isMulti && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Time per slide (seconds)</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  step={0.5}
                  value={cfg.intervalMs / 1000}
                  onChange={(e) =>
                    onChange({ ...cfg, intervalMs: Math.round((parseFloat(e.target.value) || 0) * 1000) })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Fade transition (seconds)</label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.1}
                  value={cfg.transitionMs / 1000}
                  onChange={(e) =>
                    onChange({ ...cfg, transitionMs: Math.round((parseFloat(e.target.value) || 0) * 1000) })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                />
              </div>
            </div>
          )}

          {cfg.items.length > 0 && (
            <ul className="space-y-2">
              {cfg.items.map((it, idx) => {
                const m = libraryById.get(it.mediaId);
                return (
                  <li
                    key={`${it.mediaId}-${idx}`}
                    draggable
                    onDragStart={(e) => {
                      setDragIdx(idx);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (dragOverIdx !== idx) setDragOverIdx(idx);
                    }}
                    onDragLeave={() => {
                      if (dragOverIdx === idx) setDragOverIdx(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIdx !== null) reorder(dragIdx, idx);
                      setDragIdx(null);
                      setDragOverIdx(null);
                    }}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setDragOverIdx(null);
                    }}
                    className={`flex items-center gap-3 p-2 bg-background border rounded-lg transition-colors ${
                      dragOverIdx === idx && dragIdx !== null && dragIdx !== idx
                        ? "border-brand"
                        : "border-border"
                    } ${dragIdx === idx ? "opacity-50" : ""}`}
                  >
                    <span
                      className="text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
                      title="Drag to reorder"
                    >
                      <GripVertical size={14} />
                    </span>
                    <span className="text-xs text-muted-foreground w-6 text-center">{idx + 1}</span>
                    <div className="w-16 h-12 rounded overflow-hidden bg-surface-alt shrink-0">
                      {m ? (
                        <img src={m.webp_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">
                          missing
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{m?.filename ?? "(deleted from library)"}</p>
                      {m && (
                        <p className="text-[11px] text-muted-foreground">
                          {m.width}×{m.height}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="p-1.5 rounded hover:bg-card disabled:opacity-30"
                        title="Move up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === cfg.items.length - 1}
                        className="p-1.5 rounded hover:bg-card disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        onClick={() => remove(idx)}
                        className="p-1.5 rounded hover:bg-card hover:text-destructive"
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
            <button
              onClick={() => setPicking(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:bg-background"
            >
              <Plus size={14} /> {cfg.items.length === 0 ? "Add photo" : "Add from library"}
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>

      {picking && (
        <MultiPickerModal
          items={library}
          existingIds={new Set(cfg.items.map((i) => i.mediaId))}
          title={`Add photos to — ${spot.label}`}
          onClose={() => setPicking(false)}
          onConfirm={(ids) => {
            addItems(ids);
            setPicking(false);
          }}
        />
      )}
      {pickingSlot !== null && (
        <MultiPickerModal
          items={library}
          existingIds={new Set()}
          title={`Pick photo for Slide ${pickingSlot + 1} — ${spot.label}`}
          onClose={() => setPickingSlot(null)}
          onConfirm={(ids) => {
            if (ids[0]) setSlot(pickingSlot, ids[0]);
            setPickingSlot(null);
          }}
        />
      )}
    </div>

  );
}




function MultiPickerModal({
  items,
  existingIds,
  title,
  onClose,
  onConfirm,
}: {
  items: MediaItem[];
  existingIds: Set<string>;
  title: string;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.filename.toLowerCase().includes(q));
  }, [items, query]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-2xl w-full max-w-4xl flex flex-col max-h-[90vh]"
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold text-ink">
            <Library size={16} /> {title}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by filename…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm"
            />
          </div>
        </div>

        <div className="p-4 overflow-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              {items.length === 0
                ? "Library is empty. Upload photos first."
                : "No matches."}
            </p>
          ) : (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {filtered.map((item) => {
                const isSel = selected.has(item.id);
                const isExisting = existingIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => !isExisting && toggle(item.id)}
                    disabled={isExisting}
                    className={`group relative bg-background border rounded-lg overflow-hidden text-left transition-colors ${
                      isExisting
                        ? "border-border opacity-50 cursor-not-allowed"
                        : isSel
                        ? "border-brand ring-2 ring-brand"
                        : "border-border hover:border-brand"
                    }`}
                  >
                    <div className="aspect-square bg-surface-alt">
                      <img
                        src={item.webp_url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs truncate font-medium" title={item.filename}>
                        {item.filename}
                      </p>
                    </div>
                    {isSel && (
                      <div className="absolute top-2 right-2 bg-brand text-primary-foreground rounded-full p-1">
                        <Check size={14} />
                      </div>
                    )}
                    {isExisting && (
                      <div className="absolute top-2 right-2 bg-muted text-foreground text-[10px] px-2 py-0.5 rounded-full">
                        added
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{selected.size} selected</p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-border text-sm hover:bg-background"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(Array.from(selected))}
              disabled={selected.size === 0}
              className="px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold disabled:opacity-50"
            >
              Add {selected.size || ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Google Drive picker
// ============================================================

function DrivePickerModal({
  onClose,
  onImport,
}: {
  onClose: () => void;
  onImport: (files: DriveFile[]) => void;
}) {
  const listFn = useServerFn(listDriveFiles);
  const [folderStack, setFolderStack] = useState<{ id: string; name: string }[]>([
    { id: "root", name: "My Drive" },
  ]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, DriveFile>>(new Map());
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);

  const currentFolder = folderStack[folderStack.length - 1];

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFiles([]);
    setNextPageToken(null);
    listFn({
      data: {
        folderId: debouncedQuery ? null : currentFolder.id,
        query: debouncedQuery || null,
        pageToken: null,
      },
    })
      .then((res) => {
        if (cancelled) return;
        setFiles(res.files);
        setNextPageToken(res.nextPageToken);
      })
      .catch((e: any) => !cancelled && setError(e?.message || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [currentFolder.id, debouncedQuery]);

  const loadMore = async () => {
    if (!nextPageToken) return;
    setLoading(true);
    try {
      const res = await listFn({
        data: {
          folderId: debouncedQuery ? null : currentFolder.id,
          query: debouncedQuery || null,
          pageToken: nextPageToken,
        },
      });
      setFiles((f) => [...f, ...res.files]);
      setNextPageToken(res.nextPageToken);
    } catch (e: any) {
      toast.error(e?.message || "Failed to load more");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (f: DriveFile) =>
    setSelected((m) => {
      const next = new Map(m);
      if (next.has(f.id)) next.delete(f.id);
      else next.set(f.id, f);
      return next;
    });

  const isFolder = (f: DriveFile) => f.mimeType === "application/vnd.google-apps.folder";

  const openFolder = (f: DriveFile) => {
    setSelected(new Map());
    setQuery("");
    setFolderStack((s) => [...s, { id: f.id, name: f.name }]);
  };

  const goBack = () => {
    if (folderStack.length <= 1) return;
    setSelected(new Map());
    setQuery("");
    setFolderStack((s) => s.slice(0, -1));
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-2xl w-full max-w-5xl flex flex-col max-h-[90vh]"
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold text-ink">
            <HardDrive size={16} /> Import from Google Drive
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={goBack}
              disabled={folderStack.length <= 1 || !!debouncedQuery}
              className="p-2 rounded-md border border-border hover:bg-background disabled:opacity-30"
              title="Back"
            >
              <ArrowLeft size={14} />
            </button>
            <nav className="flex items-center gap-1 text-sm text-muted-foreground overflow-x-auto">
              {folderStack.map((f, i) => (
                <span key={f.id} className="flex items-center gap-1 whitespace-nowrap">
                  {i > 0 && <span>/</span>}
                  <button
                    onClick={() => {
                      setSelected(new Map());
                      setQuery("");
                      setFolderStack((s) => s.slice(0, i + 1));
                    }}
                    className={
                      i === folderStack.length - 1 ? "text-foreground font-medium" : "hover:text-foreground"
                    }
                  >
                    {f.name}
                  </button>
                </span>
              ))}
            </nav>
          </div>
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your entire Drive…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm"
            />
          </div>
        </div>

        <div className="p-4 overflow-auto flex-1">
          {error && (
            <p className="text-sm text-destructive py-3">{error}</p>
          )}
          {loading && files.length === 0 ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="animate-spin text-brand" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              No photos or folders here.
            </p>
          ) : (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {files.map((f) => {
                const folder = isFolder(f);
                const isSel = selected.has(f.id);
                return (
                  <button
                    key={f.id}
                    onClick={() => (folder ? openFolder(f) : toggle(f))}
                    className={`group relative bg-background border rounded-lg overflow-hidden text-left transition-colors ${
                      isSel ? "border-brand ring-2 ring-brand" : "border-border hover:border-brand"
                    }`}
                  >
                    <div className="aspect-square bg-surface-alt flex items-center justify-center">
                      {folder ? (
                        <FolderOpen className="text-brand" size={48} />
                      ) : f.thumbnailLink ? (
                        <img
                          src={f.thumbnailLink}
                          alt={f.name}
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon className="text-muted-foreground" size={40} />
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs truncate font-medium" title={f.name}>
                        {f.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {folder ? "Folder" : f.mimeType.replace("image/", "")}
                      </p>
                    </div>
                    {isSel && (
                      <div className="absolute top-2 right-2 bg-brand text-primary-foreground rounded-full p-1">
                        <Check size={14} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {nextPageToken && (
            <div className="flex justify-center mt-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-background disabled:opacity-50"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {selected.size} photo{selected.size === 1 ? "" : "s"} selected
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-border text-sm hover:bg-background"
            >
              Cancel
            </button>
            <button
              onClick={() => onImport(Array.from(selected.values()))}
              disabled={selected.size === 0}
              className="px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold disabled:opacity-50"
            >
              Import {selected.size || ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
